import React, { useState, useRef, useMemo } from "react";
import styles from "./style.module.scss";
import { Checkbox, Select, Tooltip, notification } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { apiRequest } from "util/services";
import { useTranslation } from "react-i18next";
import firebase from "FirebaseConfig.js";
const { Option } = Select;

const ANOMALY_DEFAULT_CONDITION = "above";
const ANOMALY_DEFAULT_THRESHOLD = 0;
const ANOMALY_DEFAULT_FREQUENCY = ["daily"];
const ANOMALY_FREQUENCY_OPTIONS = ["daily", "weekly"];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const frequencyOptionLabel = (value, t) => {
  if (value === "daily") {
    return t("team_kpi_view.frequency_daily", "Daily");
  }
  if (value === "weekly") {
    return t("team_kpi_view.frequency_weekly", "Weekly");
  }
  return String(value ?? "");
};

const isValidEmail = (value) => EMAIL_REGEX.test(String(value ?? "").trim());

const AlertPopup = ({
  theme,
  close,
  type,
  handleAction,
  disable,
  editFrequency,
  editUsers,
  editCondition,
  editThreshold,
  editSendSMS,
  variant = "default",
  subtopics = [],
  kpiTitle = "",
  alertLimitsRemaining = null,
  alertLimitsMax = null,
}) => {
  const { t } = useTranslation();
  const isAnomaly = variant === "anomaly";
  const subtopicList = Array.isArray(subtopics) ? subtopics : [];
  const hasSubtopics = isAnomaly && type === "add" && subtopicList.length > 0;
  const [users, setUsers] = useState(type === "edit" ? editUsers : []);
  const [usersList, setUsersList] = useState([]);
  const [condition, setCondition] = useState(
    type === "edit" ? editCondition : ""
  );
  const [frequency, setFrequency] = useState(() => {
    if (type === "edit" && Array.isArray(editFrequency) && editFrequency.length) {
      if (variant === "anomaly") {
        const filtered = editFrequency.filter((f) =>
          ANOMALY_FREQUENCY_OPTIONS.includes(f)
        );
        return filtered.length ? filtered : ANOMALY_DEFAULT_FREQUENCY;
      }
      return editFrequency;
    }
    if (variant === "anomaly") {
      return ANOMALY_DEFAULT_FREQUENCY;
    }
    return [t("alerts_page.36"), t("alerts_page.37")];
  });
  const [newEmailValue, setNewEmailValue] = useState("");
  const [emailInputError, setEmailInputError] = useState("");
  const [showAddEmailField, setShowAddEmailField] = useState(true);
  const [thresholdValue, setThresholdValue] = useState(
    type === "edit" ? editThreshold : 0
  );
  const [smsBoxChecked, setSmsBoxChecked] = useState(
    type === "edit" ? editSendSMS : false
  );
  const [includeSubtopics, setIncludeSubtopics] = useState(false);
  const [subtopicAccordionOpen, setSubtopicAccordionOpen] = useState(true);
  const [selectedSubtopicKeys, setSelectedSubtopicKeys] = useState([]);
  const emailInputRef = useRef();
  const inputRef = useRef();
  const myEmail = String(firebase.auth().currentUser?.email || "").trim();
  const myEmailAlreadyAdded = users.some(
    (user) => String(user).trim().toLowerCase() === myEmail.toLowerCase()
  );

  const subtopicKeyFor = (row) => String(row?.id ?? row?.label ?? "");

  const selectedSubtopicCount = useMemo(() => {
    if (!includeSubtopics) return 0;
    const allowed = new Set(subtopicList.map(subtopicKeyFor));
    return selectedSubtopicKeys.filter((key) => allowed.has(String(key))).length;
  }, [includeSubtopics, selectedSubtopicKeys, subtopicList]);

  const totalAlertPlans = 1 + selectedSubtopicCount;

  const frequencySummary = useMemo(() => {
    const values = (Array.isArray(frequency) ? frequency : [])
      .filter((f) => ANOMALY_FREQUENCY_OPTIONS.includes(f))
      .map((f) => frequencyOptionLabel(f, t));
    return values.length ? values.join("+") : "—";
  }, [frequency, t]);

  const toggleSubtopicSelection = (key) => {
    const normalized = String(key);
    setSelectedSubtopicKeys((prev) =>
      prev.includes(normalized)
        ? prev.filter((item) => item !== normalized)
        : [...prev, normalized],
    );
  };

  const selectAllSubtopics = () => {
    setSelectedSubtopicKeys(subtopicList.map(subtopicKeyFor));
  };

  const clearSubtopicSelection = () => {
    setSelectedSubtopicKeys([]);
  };

  const handleIncludeSubtopicsChange = (checked) => {
    setIncludeSubtopics(checked);
    if (checked) {
      setSubtopicAccordionOpen(true);
    }
  };

  const addEmailToList = (rawValue) => {
    const value = String(rawValue ?? "").trim();
    if (!value) return true;
    if (!isValidEmail(value)) return false;
    setUsers((prev) =>
      prev.some((user) => String(user).trim().toLowerCase() === value.toLowerCase())
        ? prev
        : [...prev, value]
    );
    setNewEmailValue("");
    setEmailInputError("");
    return true;
  };

  const handleAddMyEmail = () => {
    if (!myEmail) return;
    addEmailToList(myEmail);
  };

  const resolveUsersForSubmit = () => {
    const pending = newEmailValue.trim();
    if (!pending) {
      return { ok: true, users: users };
    }
    if (!isValidEmail(pending)) {
      return { ok: false, users: users };
    }
    const merged = users.includes(pending) ? users : [...users, pending];
    setNewEmailValue("");
    setEmailInputError("");
    return { ok: true, users: merged };
  };

  const handleAlert = () => {
    if (isAnomaly) {
      const resolved = resolveUsersForSubmit();
      if (!resolved.ok) {
        setEmailInputError(
          t("team_kpi_view.email_invalid", "Enter a valid email address or clear the field")
        );
        return;
      }
      if (!resolved.users || resolved.users.length === 0) {
        notification.error({
          message: t("team_kpi_view.56"),
        });
        return;
      }
      const conditionValue =
        type === "edit" && editCondition
          ? editCondition
          : ANOMALY_DEFAULT_CONDITION;
      const thresholdValueResolved =
        type === "edit" && editThreshold != null
          ? editThreshold
          : ANOMALY_DEFAULT_THRESHOLD;
      const frequencyValue = (
        Array.isArray(frequency) ? frequency : []
      ).filter((f) => ANOMALY_FREQUENCY_OPTIONS.includes(f));
      if (!frequencyValue.length) {
        notification.error({
          message: t("team_kpi_view.77"),
        });
        return;
      }
      const smsValue = type === "edit" ? Boolean(editSendSMS) : false;
      handleAction(
        conditionValue,
        thresholdValueResolved,
        resolved.users,
        frequencyValue,
        smsValue,
        includeSubtopics,
        includeSubtopics ? selectedSubtopicKeys : null,
      );
      return;
    }

    const resolved = resolveUsersForSubmit();
    if (!resolved.ok) {
      setEmailInputError(
        t("team_kpi_view.email_invalid", "Enter a valid email address or clear the field")
      );
      return;
    }

    if (!condition || !thresholdValue || !frequency) {
      notification.error({
        message: t("notifications.148"),
      });
    } else if (isNaN(thresholdValue)) {
      notification.error({
        message: t("notifications.149"),
      });
    } else {
      handleAction(
        condition,
        thresholdValue,
        resolved.users,
        frequency,
        smsBoxChecked
      );
    }
  };

  const handleEmailInputChange = (e) => {
    const raw = e.target.value;
    const lastChar = raw.slice(-1);
    if (lastChar === "," || lastChar === " ") {
      const candidate = raw.slice(0, -1).trim();
      if (candidate && isValidEmail(candidate)) {
        setUsers((prev) =>
          prev.includes(candidate) ? prev : [...prev, candidate]
        );
        setNewEmailValue("");
        setEmailInputError("");
        return;
      }
    }
    setNewEmailValue(raw);
    if (emailInputError) setEmailInputError("");
  };

  const handleEmailBlur = () => {
    const value = newEmailValue.trim();
    if (!value) return;
    if (isValidEmail(value)) {
      addEmailToList(value);
    }
  };

  const handleEmailKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const value = newEmailValue.trim();
    if (!value) {
      notification.error({
        message: t(
          "team_kpi_view.email_invalid",
          "Enter a valid email address or clear the field"
        ),
      });
      return;
    }
    if (!isValidEmail(value)) {
      setEmailInputError(
        t("team_kpi_view.email_invalid", "Enter a valid email address or clear the field")
      );
      return;
    }
    addEmailToList(value);
  };

  const handleRemoveUser = (val) => {
    setUsers(users?.filter((user) => user !== val));
  };

  return (
    <div className={styles.overlayContainer}>
      <div className={styles.centerWrapper}>
        <div
          className={`${styles.alertContent} ${
            theme === "dark" ? styles.alertContentDark : ""
          } ${isAnomaly ? styles.anomalyAlertContent : ""}`}
        >
          <div className={styles.title}>
            <span>
              {isAnomaly
                ? t("team_kpi_view.43")
                : type === "add"
                ? t("alerts_page.39")
                : t("alerts_page.40")}
            </span>
            <div className={styles.titleActions}>
              {isAnomaly ? (
                <Tooltip
                  placement="bottomLeft"
                  color="var(--ink)"
                  overlayClassName={styles.anomalyInfoTooltip}
                  title={
                    <div className={styles.anomalyInfoTooltipBody}>
                      <p className={styles.anomalyInfoTooltipTitle}>
                        {t(
                          "team_kpi_view.alert_info_title",
                          "How alerts fire"
                        )}
                      </p>
                      <ul className={styles.anomalyInfoTooltipList}>
                        <li className={styles.anomalySeverityCritical}>
                          {t(
                            "team_kpi_view.alert_info_critical",
                            "Critical: 2+ pain metrics rise (negative %, complaint %). Email."
                          )}
                        </li>
                        <li className={styles.anomalySeverityWarning}>
                          {t(
                            "team_kpi_view.alert_info_warning",
                            "Warning: 1 pain metric rises. Email."
                          )}
                        </li>
                        <li className={styles.anomalySeverityInfo}>
                          {t(
                            "team_kpi_view.alert_info_volume",
                            "Info: Volume changes only."
                          )}
                        </li>
                      </ul>
                      <p className={styles.anomalyInfoTooltipNote}>
                        {t("team_kpi_view.80")}
                      </p>
                    </div>
                  }
                >
                  <button
                    type="button"
                    className={styles.titleInfoBtn}
                    aria-label={t(
                      "team_kpi_view.alert_info_aria",
                      "How alerts fire"
                    )}
                  >
                    <InfoCircleOutlined />
                  </button>
                </Tooltip>
              ) : null}
              <img
                src={theme === "dark" ? "/assets/x.svg" : "/assets/x_black.svg"}
                onClick={() => close(false)}
                alt=""
              />
            </div>
          </div>
          {isAnomaly && (
            <p className={styles.anomalyIntro}>{t("team_kpi_view.55")}</p>
          )}
          {isAnomaly && (
            <div className={`${styles.dropdownBox} ${styles.anomalySection}`}>
              <p className={styles.label}>{t("team_kpi_view.77")}</p>
              <Select
                mode="multiple"
                className={styles.frequencySelect}
                popupClassName={styles.frequencyDropdown}
                getPopupContainer={() => document.body}
                dropdownStyle={{
                  zIndex: 1100,
                  backgroundColor: "var(--popup)",
                }}
                popupMatchSelectWidth={false}
                optionLabelProp="label"
                virtual={false}
                listHeight={180}
                style={{ width: "100%" }}
                placeholder={t("team_kpi_view.77")}
                value={frequency}
                onChange={(value) => setFrequency(value)}
                showArrow
                maxTagCount={2}
                tagRender={({ label, value, closable, onClose }) => (
                  <span className={styles.freqTag}>
                    {frequencyOptionLabel(value ?? label, t)}
                    {closable ? (
                      <button
                        type="button"
                        className={styles.freqTagRemove}
                        onClick={onClose}
                        aria-label={t("button.remove", "Remove")}
                      >
                        ×
                      </button>
                    ) : null}
                  </span>
                )}
              >
                <Option
                  value="daily"
                  key="daily"
                  label={t("team_kpi_view.frequency_daily", "Daily")}
                >
                  <span className={styles.freqOption}>
                    <span className={styles.freqOptionTitle}>
                      {t("team_kpi_view.frequency_daily", "Daily")}
                    </span>
                    <span className={styles.freqOptionDesc}>
                      {t("team_kpi_view.78")}
                    </span>
                  </span>
                </Option>
                <Option
                  value="weekly"
                  key="weekly"
                  label={t("team_kpi_view.frequency_weekly", "Weekly")}
                >
                  <span className={styles.freqOption}>
                    <span className={styles.freqOptionTitle}>
                      {t("team_kpi_view.frequency_weekly", "Weekly")}
                    </span>
                    <span className={styles.freqOptionDesc}>
                      {t("team_kpi_view.79")}
                    </span>
                  </span>
                </Option>
              </Select>
            </div>
          )}
          {hasSubtopics && (
            <div className={styles.subtopicsBox}>
              <Checkbox
                checked={includeSubtopics}
                onChange={(event) =>
                  handleIncludeSubtopicsChange(event.target.checked)
                }
              >
                <span className={styles.subtopicsCheckboxLabel}>
                  {t(
                    "team_kpi_view.alert_include_subtopics",
                    "Create alerts for subtopics too"
                  )}
                </span>
              </Checkbox>
              <p className={styles.subtopicsHint}>
                {t("team_kpi_view.alert_include_subtopics_hint_select", {
                  title: kpiTitle || t("team_kpi_view.54", "Alert"),
                  defaultValue:
                    "An alert is created for {{title}}; choose which subtopics to include below.",
                })}
              </p>
              {includeSubtopics ? (
                <div className={styles.subtopicAccordion}>
                  <div className={styles.subtopicAccordionHeader}>
                    <button
                      type="button"
                      className={styles.subtopicAccordionToggle}
                      onClick={() =>
                        setSubtopicAccordionOpen((open) => !open)
                      }
                      aria-expanded={subtopicAccordionOpen}
                    >
                      <span
                        className={`${styles.subtopicAccordionChevron} ${
                          subtopicAccordionOpen
                            ? styles.subtopicAccordionChevronOpen
                            : ""
                        }`}
                        aria-hidden
                      >
                        ▶
                      </span>
                      <span>
                        {t("team_kpi_view.alert_subtopics_accordion_title", {
                          title: kpiTitle || t("team_kpi_view.54", "Alert"),
                          defaultValue: "{{title}} subtopics",
                        })}
                      </span>
                      <span className={styles.subtopicSelectionBadge}>
                        {t("team_kpi_view.alert_subtopics_selected_badge", {
                          selected: selectedSubtopicCount,
                          total: subtopicList.length,
                          defaultValue: "{{selected}} / {{total}} selected",
                        })}
                      </span>
                    </button>
                    <div className={styles.subtopicAccordionActions}>
                      <button
                        type="button"
                        className={styles.subtopicActionBtn}
                        onClick={selectAllSubtopics}
                      >
                        {t(
                          "team_kpi_view.alert_subtopics_select_all",
                          "Select all"
                        )}
                      </button>
                      <button
                        type="button"
                        className={styles.subtopicActionBtn}
                        onClick={clearSubtopicSelection}
                      >
                        {t("team_kpi_view.alert_subtopics_clear", "Clear")}
                      </button>
                    </div>
                  </div>
                  {subtopicAccordionOpen ? (
                    <div className={styles.subtopicAccordionBody}>
                      {subtopicList.map((row) => {
                        const key = subtopicKeyFor(row);
                        const checked = selectedSubtopicKeys.includes(key);
                        return (
                          <label
                            key={key}
                            className={styles.subtopicRow}
                          >
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleSubtopicSelection(key)}
                            />
                            <span className={styles.subtopicRowLabel}>
                              {row.label}
                            </span>
                            <span className={styles.subtopicRowMeta}>
                              {checked ? frequencySummary : "—"}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {includeSubtopics && alertLimitsRemaining != null ? (
                <p
                  className={`${styles.subtopicsQuotaNote} ${
                    alertLimitsRemaining < totalAlertPlans
                      ? styles.subtopicsQuotaWarn
                      : ""
                  }`}
                >
                  {t("team_kpi_view.alert_subtopics_quota_selected", {
                    total: totalAlertPlans,
                    count: selectedSubtopicCount,
                    remaining: alertLimitsRemaining,
                    max: alertLimitsMax ?? alertLimitsRemaining,
                    defaultValue:
                      "Selected: 1 parent + {{count}} subtopics = {{total}} alerts · Quota remaining: {{remaining}} / {{max}}",
                  })}
                </p>
              ) : null}
            </div>
          )}
          {!isAnomaly && (
          <div className={styles.dropdownBox}>
            <p className={styles.label}>{t("alerts_page.7")}</p>
            <Select
              style={{ width: "100%" }}
              placeholder={t("alerts_page.19")}
              value={condition}
              onChange={(value) => setCondition(value)}
            >
              <Option value="above" key={"above"}>
                {t("alerts_page.34")}
              </Option>
              <Option value="below" key={"below"}>
                {t("alerts_page.35")}
              </Option>
            </Select>
          </div>
          )}
          {!isAnomaly && (
          <div className={styles.fieldsWrapper}>
            <div className={styles.inputBox}>
              <p className={styles.label}>{t("alerts_page.8")}(%)</p>
              <input
                type="text"
                value={thresholdValue}
                onChange={(e) => setThresholdValue(e.target.value)}
                placeholder={t("label.16")}
              />
            </div>
            <div className={`${styles.dropdownBox} ${styles.frequencyBox}`}>
              <p className={styles.label}>{t("alerts_page.27")}</p>
              <Select
                placeholder={t("alerts_page.28")}
                style={{ minWidth: 100 }}
                mode="multiple"
                value={frequency}
                onChange={(value) => setFrequency(value)}
                showArrow
                maxTagCount="responsive"
              >
                <Option value="hourly" key={"hourly"}>
                  {t("alerts_page.38")}
                </Option>
                <Option value="daily" key={"daily"}>
                  {t("alerts_page.37")}
                </Option>
                <Option value="weekly" key={"weekly"}>
                  {t("alerts_page.36")}
                </Option>
              </Select>
            </div>
          </div>
          )}

          <div
            className={`${styles.dropdownBox} ${
              isAnomaly ? styles.anomalySection : ""
            }`}
          >
            <p className={styles.label}>
              {isAnomaly ? t("team_kpi_view.52") : t("alerts_page.17")}
            </p>
            <div className={styles.userListBox}>
              {users?.map((user) => (
                <span className={styles.userTag} key={user}>
                  <span title={user} className={styles.userEmail}>
                    {user}
                  </span>{" "}
                  <img
                    src={"/assets/x.svg"}
                    onClick={() => handleRemoveUser(user)}
                    alt=""
                  />
                </span>
              ))}
            </div>
            {showAddEmailField && (
              <div className={styles.inputWrapper} ref={emailInputRef}>
                <input
                  ref={inputRef}
                  className={styles.inputField}
                  onKeyDown={handleEmailKeyDown}
                  onBlur={handleEmailBlur}
                  placeholder={
                    isAnomaly
                      ? t(
                          "team_kpi_view.email_placeholder",
                          "name@example.com — press Enter to add"
                        )
                      : t("alerts_page.33")
                  }
                  value={newEmailValue}
                  onChange={handleEmailInputChange}
                />
                {emailInputError ? (
                  <p className={styles.emailInputError}>{emailInputError}</p>
                ) : null}
                {myEmail && !myEmailAlreadyAdded ? (
                  <button
                    type="button"
                    className={styles.addMyEmailBtn}
                    onClick={handleAddMyEmail}
                  >
                    {t("team_kpi_view.add_my_email", "Add my email")}
                  </button>
                ) : null}
              </div>
            )}
            {(!isAnomaly || !showAddEmailField) && (
              <div className={styles.addEmailBlock}>
                +{" "}
                <span onClick={() => setShowAddEmailField(true)}>
                  {t("alerts_page.32")}
                </span>
              </div>
            )}
          </div>
          {!isAnomaly && (
          <div className={styles.checkBox}>
            <Checkbox
              checked={smsBoxChecked}
              onClick={() => setSmsBoxChecked(!smsBoxChecked)}
            >
              <p className={styles.checkboxLabel}>{t("alerts_page.41")}</p>
            </Checkbox>
          </div>
          )}
          <button
            className={styles.addAlertBtn}
            disabled={disable}
            onClick={handleAlert}
          >
            {isAnomaly
              ? type === "add"
                ? t("team_kpi_view.57")
                : t("button.15")
              : t("alerts_page.42")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertPopup;
